import { isObject } from "@gr-common/async/tools";
import {
  dispatchNavigationEvents,
  getCurrentNavigation,
  provideNavigationInfo,
} from "./navigation";
import { asyncScope, type ObjectToEntries } from "./utils";
import type { HistoryThis, InvocationContext } from "./history.types";
import { sanitizeIncommingState } from "../data/state";

const log = asyncScope.scope("history");

/**
 *
 *
 */
export const $context = Symbol("context");

/**
 *
 *
 *
 *
 *
 */
export const getContext = (obj: unknown): InvocationContext =>
  isObject(obj) && $context in obj && isObject(obj[$context]) ? obj[$context] : {};

/**
 *
 */
export const defineContext = (context: InvocationContext): HistoryThis =>
  ({ [$context]: context }) as HistoryThis;

/**
 *
 */
export const getPushStateHandler = (
  history: History,
): ((this: HistoryThis, data: unknown, unused: string, url?: string | URL | null) => void) => {
  const pushStateCall = history.pushState.bind(history);

  log.debug("Create pushState handler");
  return function pushStateHandler(this, incommingData, unused, incommingUrl) {
    const { scope, silent, clear, info } = getContext(this);
    log.info("Process pushState incvocation", scope, info);

    /*                                                               */
    /*                                                                 */
    const stateData = sanitizeIncommingState(history.state, incommingData, clear);

    /*                              */
    pushStateCall(stateData, unused, incommingUrl);

    /*                                       */
    if (silent) return;

    const nav = getCurrentNavigation(incommingUrl);
    dispatchNavigationEvents(nav, "push", info);
  };
};

/**
 *
 */
export const getReplaceStateHandler = (
  history: History,
): ((this: HistoryThis, data: unknown, unused: string, url?: string | URL | null) => void) => {
  const replaceStateCall = history.replaceState.bind(history);

  log.debug("Create replaceState handler");
  return function replaceStateHandler(this, incommingData, unused, incommingUrl) {
    const { scope, silent, clear, info } = getContext(this);
    log.info("Process replaceState invocation", scope, info);

    /*                                                               */
    /*                                                                 */
    const stateData = sanitizeIncommingState(history.state, incommingData, clear);

    /*                              */
    replaceStateCall(stateData, unused, incommingUrl);

    /*                                       */
    if (silent) return;

    const nav = getCurrentNavigation(incommingUrl);
    dispatchNavigationEvents(nav, "replace", info);
  };
};

/**
 *
 */
export const getForwardHandler = (history: History): ((this: HistoryThis) => void) => {
  const forward = history.forward.bind(history);

  log.debug("Create forward handler");
  return function forwardHandler(this) {
    const { scope, info } = getContext(this);
    log.info("Process forward invocation", scope, info);

    /*                                                         */
    provideNavigationInfo(info);

    /*                              */
    forward();
  };
};

/**
 *
 */
export const getBackHandler = (history: History): ((this: HistoryThis) => void) => {
  const back = history.back.bind(history);

  log.debug("Create back handler");
  return function backHandler(this) {
    const { scope, info } = getContext(this);
    log.info("Process back invocation", scope, info);

    /*                              */
    back();

    /*                                                         */
    provideNavigationInfo(info);
  };
};

/**
 *
 */
export const getGoHandler = (history: History): ((this: HistoryThis, delata?: number) => void) => {
  const go = history.go.bind(history);

  log.debug("Create go handler");
  return function goHandler(this, delta) {
    const { scope, info } = getContext(this);
    log.info("Process go invocation", scope, info);

    /*                              */
    go(delta);

    /*                                                         */
    provideNavigationInfo(info);
  };
};

/**
 *
 *
 *
 *
 *
 */
export function patchHistory(history = window.history): void {
  log.debug("Patch history object");
  Object.defineProperties(
    window.history,
    Object.fromEntries(
      (
        [
          ["pushState", getPushStateHandler(history)],
          ["replaceState", getReplaceStateHandler(history)],
          ["forward", getForwardHandler(history)],
          ["back", getBackHandler(history)],
          ["go", getGoHandler(history)],
        ] satisfies ObjectToEntries<History>
      ).map(([key, value]) => [key, { value, writable: true, configurable: true }]),
    ),
  );
}
